import { useEffect, useRef } from "react"
import type { RootState } from "@/store"
import type { ItemConta } from "@/api"
import { Alert, Box, Card, Chip, Divider, Stack, styled, Typography } from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import { blue, lime, red } from "@mui/material/colors"
import { useSelector } from "react-redux"
import { capitalize, formatarBRL, formatarData } from "@/lib/formato"
import { calcularConta } from "./calculo-conta"
import TaxaServicoCard from "./taxa-servico-card"
import BotaoTaxaServico from "./botao-taxa-servico"
import type { TaxaServicoTotem } from "./use-taxa-servico-totem"
import { LinhaItemPedido } from "@/components/lista-itens-pedido"
import { useImagensPorProduto } from "@/lib/imagens-catalogo"
import DiscountIcon from '@mui/icons-material/Discount'
import LinhasDoLeitor from "./linhas-leitor"
import type { LeituraNaTela } from "./use-leitor-resumo"

const PREFIX = 'OrderSummary'

const classes = {
    desconto: `${PREFIX}-desconto`,
    taxaServico: `${PREFIX}-taxaServico`,
    subTotal: `${PREFIX}-subTotal`,
}

const StyledTypography = styled(Typography)(({ theme }) => ({
    [`&.${classes.desconto}`]: {
        color: theme.palette.mode === 'dark' ? red[400] : red[800],
    },
    [`&.${classes.taxaServico}`]: {
        color: theme.palette.mode === 'dark' ? lime[400] : lime[800],
    },
    [`&.${classes.subTotal}`]: {
        color: theme.palette.mode === 'dark' ? blue[400] : blue[600],
    },
}))

export function OrderItemCard({ item, imagemUrl }: { item: ItemConta; imagemUrl?: string }): JSX.Element {
    const subitens = (item.subItens ?? []).map((sub) => ({
        nome: sub.nome ?? '',
        quantidade: sub.quantidade ?? 1,
        valorTotal: sub.valorTotal ?? 0,
    }))

    return (
        <LinhaItemPedido
            tamanho="grande"
            item={{
                id: item.codigoExterno ?? (item.nome ?? 'item'),
                nome: item.nome ?? '',
                quantidade: item.quantidade ?? 1,
                valorTotal: item.valorTotal ?? 0,
                imagemUrl,
                observacao: item.observacao,
                subitens,
            }}
        />
    )
}

interface ItemsBodyProps {
    taxa: TaxaServicoTotem
    bloqueado?: boolean
    leituras?: LeituraNaTela[]
    dicaLeitor?: boolean
    leitorPausado?: boolean
}

function ItemsBody({ taxa, bloqueado = false, leituras = [], dicaLeitor = false, leitorPausado = false }: ItemsBodyProps): JSX.Element | null {
    const theme = useTheme()
    const order = useSelector((state: RootState) => state.selfServiceKiosk.order)
    const modeloIdentificador = useSelector((state: RootState) => state.selfServiceKiosk.modeloIdentificador)
    const identificadorBuscado = useSelector((state: RootState) => state.selfServiceKiosk.identificadorBuscado)
    const imagemPorProduto = useImagensPorProduto('CARDAPIO_TOTEM_CHECKOUT')
    const rotuloPrincipal = capitalize(modeloIdentificador)
    const rotuloOposto = modeloIdentificador === 'COMANDA' ? 'Mesa' : 'Comanda'
    const itens = order?.itens ?? []
    const abertoAs = order?.dataPedido ? formatarData(order.dataPedido, 'hora') : null
    const { subtotal, desconto, taxaServico, total, valorRecebido, pendente } = calcularConta(order)
    const temPagamentoParcial = valorRecebido > 0 && pendente > 0
    const fimDaLista = useRef<HTMLDivElement>(null)
    const tamanhoAnterior = useRef({ leituras: leituras.length, itens: itens.length })

    // Leitura nova ou item que acabou de chegar: rola até o fim da lista, onde eles aparecem.
    useEffect(() => {
        const antes = tamanhoAnterior.current
        tamanhoAnterior.current = { leituras: leituras.length, itens: itens.length }
        if (leituras.length > antes.leituras || itens.length > antes.itens) fimDaLista.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    }, [leituras.length, itens.length])

    if (!order) return null

    return (
        <Stack
            sx={{
                p: 2,
                pt: 0,
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <Card
                elevation={0}
                sx={{
                    p: 1,
                    width: '100%',
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1.5px solid',
                    borderColor: theme.palette.personal.background,
                }}
            >
                <Stack sx={{ justifyContent: 'center', textAlign: 'center', p: 2, pb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        Resumo do Pedido
                    </Typography>

                    <Typography variant="h6" color="text.secondary">
                        {rotuloPrincipal} {identificadorBuscado}
                        {order.identificadorSecundario ? ` · ${rotuloOposto} ${order.identificadorSecundario}` : ` · Sem ${rotuloOposto.toLowerCase()}`}
                        {abertoAs ? ` · Aberta às ${abertoAs}` : ''} · {itens.length} {itens.length === 1 ? 'item' : 'itens'}
                    </Typography>
                </Stack>

                <Divider sx={{ borderStyle: 'dashed', mx: -2 }} />

                <Stack
                    spacing={1}
                    sx={{
                        p: 2,
                        mb: 1,
                        flexGrow: 1,
                        overflowY: 'auto',
                    }}
                >
                    <TaxaServicoCard taxa={taxa} />

                    {desconto > 0 && (
                        <Alert
                            icon={<DiscountIcon />}
                            sx={(theme) => {
                                const mainColor = theme.palette.mode === 'dark' ? red[400] : red[800]
                                return {
                                    width: '100%',
                                    borderRadius: 2,
                                    backgroundColor: alpha(mainColor, 0.1),
                                    '& .MuiAlert-icon': {
                                        color: mainColor,
                                    },
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                Este pedido possui um desconto de <strong style={{ color: 'inherit' }}>{formatarBRL(desconto)}</strong> distribuído em seus itens.
                            </Typography>
                        </Alert>
                    )}

                    {itens.map((item, indice) => (
                        <Box key={`${item.codigoExterno ?? 'item'}-${indice}`}>
                            <OrderItemCard
                                item={item}
                                imagemUrl={imagemPorProduto.get(Number(item.codigoExterno))}
                            />

                            {indice < itens.length - 1 && <Divider sx={{ borderStyle: 'dashed' }} />}
                        </Box>
                    ))}

                    <LinhasDoLeitor leituras={leituras} dica={dicaLeitor} pausado={leitorPausado} />
                    <Box ref={fimDaLista} />
                </Stack>

                <Stack sx={{ flexShrink: 0, px: 2, pb: 2 }}>
                    <Divider sx={{ borderStyle: 'dashed', mb: 1, mx: -2 }} />

                    <Stack spacing={1} sx={{ p: 1 }}>
                        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                            <StyledTypography variant="body1" className={classes.subTotal}>
                                (+) Subtotal
                            </StyledTypography>

                            <StyledTypography variant="body1" className={classes.subTotal}>
                                {formatarBRL(subtotal)}
                            </StyledTypography>
                        </Stack>

                        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                            <StyledTypography variant="body1" className={classes.desconto}>
                                (-) Desconto
                            </StyledTypography>

                            <StyledTypography variant="body1" className={classes.desconto}>
                                {formatarBRL(desconto)}
                            </StyledTypography>
                        </Stack>

                        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                            <StyledTypography variant="body1" className={classes.taxaServico}>
                                (+) Taxa de serviço
                            </StyledTypography>

                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                {taxa.dispensada && taxa.taxaServicoAnterior > 0 && (
                                    <>
                                        <Chip size="small" label="Dispensada" variant="outlined" />

                                        <Typography
                                            variant="body1"
                                            color="text.disabled"
                                            sx={{ textDecoration: 'line-through' }}
                                        >
                                            {formatarBRL(taxa.taxaServicoAnterior)}
                                        </Typography>
                                    </>
                                )}

                                <StyledTypography variant="body1" className={classes.taxaServico}>
                                    {formatarBRL(taxaServico)}
                                </StyledTypography>
                            </Stack>
                        </Stack>

                        {!bloqueado && <BotaoTaxaServico taxa={taxa} />}
                    </Stack>

                    <Divider sx={{ pb: 1, px: 2, borderColor: (theme) => theme.palette.divider }} />

                    {temPagamentoParcial && (
                        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', px: 1, pt: 2 }}>
                            <Typography variant="h6" color="text.secondary">
                                Total da conta
                            </Typography>

                            <Typography variant="h6" color="text.secondary">
                                {formatarBRL(total)}
                            </Typography>
                        </Stack>
                    )}

                    {temPagamentoParcial && (
                        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
                            <Typography variant="h6" color="error.main" sx={{ fontWeight: 600 }}>
                                Já pago
                            </Typography>

                            <Typography variant="h6" color="error.main" sx={{ fontWeight: 600 }}>
                                − {formatarBRL(valorRecebido)}
                            </Typography>
                        </Stack>
                    )}

                    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', px: 1, pb: 1, pt: 2 }}>
                        <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                            {temPagamentoParcial ? 'Falta pagar' : 'Total'}
                        </Typography>

                        <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                            {formatarBRL(temPagamentoParcial ? pendente : total)}
                        </Typography>
                    </Stack>
                </Stack>
            </Card>
        </Stack>
    )
}

export default ItemsBody
